import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BellRing, CalendarClock, CopyPlus, Crosshair, Download, FileText, History, KeyRound, MapPin, Move, Radar, RotateCcw, Save, ShieldAlert, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { API, api, formatApiError } from "@/lib/api";
import { LocationSearch } from "@/components/LocationSearch";
import { AdminDragMap } from "@/components/AdminDragMap";
import { useAuth } from "@/context/AuthContext";

/* gold & navy admin theme */
const NAVY = "#0B1B33";
const NAVY_2 = "#122744";
const GOLD = "#C8A24A";
const card = "border border-[#C8A24A]/25 bg-[#122744] p-5";
const label = "text-[10px] font-bold uppercase tracking-[0.2em] text-[#C8A24A]";
const field =
  "w-full border border-[#C8A24A]/30 bg-[#0B1B33] px-3 py-2 text-sm text-[#F3E7C9] outline-none transition-colors duration-150 focus:border-[#C8A24A]";
const goldBtn =
  "flex items-center justify-center gap-2 bg-[#C8A24A] px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#0B1B33] transition-opacity duration-150 hover:opacity-85 disabled:opacity-40";
const ghostBtn =
  "flex items-center justify-center gap-2 border border-[#C8A24A]/40 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#C8A24A] transition-colors duration-150 hover:bg-[#C8A24A]/10";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUSES = ["parked", "idle", "on_trip", "maintenance"];

const weekStart = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

const iso = (day, hhmm) => {
  const [h, m] = hhmm.split(":");
  const d = new Date(day);
  d.setHours(Number(h), Number(m), 0, 0);
  return d.toISOString();
};

export default function AdminTools() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState("");

  // positioning
  const [posVehicle, setPosVehicle] = useState("");
  const [pct, setPct] = useState(0);

  // planner
  const [planVehicles, setPlanVehicles] = useState([]);
  const [planStatus, setPlanStatus] = useState("parked");
  const [planDay, setPlanDay] = useState(0);
  const [from, setFrom] = useState("06:00");
  const [to, setTo] = useState("18:00");
  const [place, setPlace] = useState(null);
  const [note, setNote] = useState("");

  const [settings, setSettings] = useState(null);
  const [filters, setFilters] = useState({ vehicle_id: "", action: "", date_from: "", date_to: "" });
  const [actions, setActions] = useState([]);
  const [undoPos, setUndoPos] = useState(null);
  const [report, setReport] = useState(null);
  const [devices, setDevices] = useState([]);
  const [revealed, setRevealed] = useState({});
  const [views, setViews] = useState([]);
  const [viewName, setViewName] = useState("");
  const [alerts, setAlerts] = useState([]);

  const auditQuery = useCallback(
    (extra = {}) => {
      const p = new URLSearchParams();
      Object.entries({ ...filters, ...extra }).forEach(([k, v]) => v && p.set(k, v));
      return p.toString();
    },
    [filters]
  );

  const load = useCallback(async () => {
    const [v, s, a, st, acts, rep, dev, vw, al] = await Promise.all([
      api.get("/vehicles"),
      api.get("/status-schedules"),
      api.get(`/audit-log?limit=100&${auditQuery()}`),
      api.get("/settings"),
      api.get("/audit-log/actions"),
      api.get("/fleet/snap-report?days=7"),
      api.get("/hardware/devices"),
      api.get("/audit-views"),
      api.get("/alerts?only_unacked=true&types=tracker-drift,tracker-offline"),
    ]);
    setVehicles(v.data);
    setSchedules(s.data);
    setLog(a.data);
    setSettings(st.data);
    setActions(acts.data);
    setReport(rep.data);
    setDevices(dev.data);
    setViews(vw.data);
    setAlerts(al.data);
  }, [auditQuery]);

  useEffect(() => {
    load().catch(() => toast.error("Could not load admin data"));
  }, [load]);

  const selected = useMemo(() => vehicles.find((v) => v.id === posVehicle), [vehicles, posVehicle]);
  const trip = selected?.active_trip;

  useEffect(() => {
    if (trip?.planned_distance_km) {
      const done = trip.distance_covered_km || 0;
      setPct(Math.round((done / trip.planned_distance_km) * 100));
    }
  }, [trip?.planned_distance_km, trip?.distance_covered_km]);

  const applyPosition = async (body, msg) => {
    setBusy("pos");
    try {
      const { data } = await api.patch(`/vehicles/${posVehicle}/route-position`, body);
      toast.success(`${msg} · ${data.trip.distance_covered_km} km covered, ${data.trip.distance_remaining_km} km left`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const savePlan = async (e) => {
    e.preventDefault();
    if (planVehicles.length === 0) return toast.error("Pick at least one truck");
    const day = new Date(weekStart());
    day.setDate(day.getDate() + planDay);
    setBusy("plan");
    try {
      await api.post("/status-schedules", {
        vehicle_ids: planVehicles,
        status: planStatus,
        from_at: iso(day, from),
        to_at: iso(day, to),
        location: place ? { lat: place.lat, lng: place.lng } : null,
        location_name: place?.name || null,
        note: note || null,
      });
      toast.success(`${planVehicles.length} truck(s) scheduled ${planStatus.replace("_", " ")} on ${DAYS[planDay]}`);
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const removePlan = async (id) => {
    try {
      await api.delete(`/status-schedules/${id}`);
      toast.success("Schedule removed");
      await load();
    } catch {
      toast.error("Could not remove schedule");
    }
  };

  const copyWeek = async () => {
    setBusy("copy");
    try {
      const { data } = await api.post("/status-schedules/copy-week", {
        vehicle_ids: planVehicles.length ? planVehicles : null,
        weeks: 1,
      });
      toast.success(`${data.copied} window(s) copied to next week`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const exportAudit = async () => {
    setBusy("csv");
    try {
      const { data } = await api.get(`/audit-log/export?limit=500&${auditQuery()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `doot-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit log exported");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const toggleAutoSnap = async () => {
    const next = !settings?.auto_snap_enabled;
    setBusy("snap");
    setSettings((s) => ({ ...(s || {}), auto_snap_enabled: next }));
    try {
      const { data } = await api.patch("/settings", { auto_snap_enabled: next });
      setSettings(data);
      toast.success(`Auto snap watch ${next ? "enabled" : "disabled"}`);
    } catch (err) {
      setSettings((s) => ({ ...(s || {}), auto_snap_enabled: !next }));
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const dropPin = async ({ lat, lng }) => {
    const before = selected?.current_location ? { ...selected.current_location } : null;
    setBusy("drag");
    try {
      const { data } = await api.patch(`/vehicles/${posVehicle}/location`, { lat, lng });
      if (before) setUndoPos({ vehicle_id: posVehicle, plate: data.plate_number, ...before });
      toast.success(`${data.plate_number} repositioned and snapped to the road`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      await load();
    } finally {
      setBusy("");
    }
  };

  const undoDrag = async () => {
    if (!undoPos) return;
    setBusy("undo");
    try {
      await api.patch(`/vehicles/${undoPos.vehicle_id}/location`, { lat: undoPos.lat, lng: undoPos.lng });
      toast.success(`${undoPos.plate} moved back to its previous position`);
      setUndoPos(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const issueKey = async (vehicleId) => {
    setBusy("key");
    try {
      const { data } = await api.post(`/hardware/devices/${vehicleId}/key`);
      try {
        await navigator.clipboard?.writeText(data.device_key);
      } catch {
        /* clipboard blocked - key is shown in the table */
      }
      toast.success(`Key issued for ${data.plate_number}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const saveView = async () => {
    if (!viewName.trim()) return toast.error("Name the view first");
    try {
      await api.post("/audit-views", { name: viewName.trim(), ...filters });
      setViewName("");
      toast.success("View saved");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const removeView = async (id) => {
    try {
      await api.delete(`/audit-views/${id}`);
      await load();
    } catch {
      toast.error("Could not delete view");
    }
  };

  const applyView = (v) =>
    setFilters({
      vehicle_id: v.vehicle_id || "",
      action: v.action || "",
      date_from: v.date_from || "",
      date_to: v.date_to || "",
    });

  const printSheet = async (vehicleId, plateNo) => {
    try {
      const { data } = await api.get(`/hardware/devices/${vehicleId}/onboarding-sheet`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/plain" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `doot-tracker-setup-${plateNo}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Setup card ready for ${plateNo}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const testPing = async (vehicleId, plateNo) => {
    setBusy("test");
    try {
      await api.post(`/hardware/devices/${vehicleId}/test-ping`);
      toast.success(`${plateNo} channel confirmed - ping accepted`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const ackAlert = async (id) => {
    try {
      await api.patch(`/alerts/${id}/ack`);
      setAlerts((a) => a.filter((x) => x.id !== id));
    } catch {
      toast.error("Could not clear alert");
    }
  };

  const revokeKey = async (vehicleId) => {    try {
      await api.delete(`/hardware/devices/${vehicleId}/key`);
      toast.success("Device key revoked");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const snapSweep = async () => {
    setBusy("sweep");
    try {
      const { data } = await api.post("/fleet/snap-sweep");
      toast.success(`${data.corrected} of ${data.checked} trucks snapped to the road`);
      await load();
    } catch {
      toast.error("Snap sweep failed");
    } finally {
      setBusy("");
    }
  };

  const plate = (id) => vehicles.find((v) => v.id === id)?.plate_number || "--";

  if (user && !["admin", "dispatcher"].includes(user.role))
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1B33] text-[#C8A24A]" data-testid="admin-denied">
        Admin access only
      </div>
    );

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#0B1B33] text-[#F3E7C9]" data-testid="admin-tools-page" style={{ background: NAVY }}>
      <header className="flex flex-wrap items-center gap-3 border-b border-[#C8A24A]/25 px-4 py-3 sm:px-8">
        <Link to="/" data-testid="admin-back-link" className={ghostBtn}>
          <ArrowLeft size={12} /> Dashboard
        </Link>
        <div className="min-w-0">
          <p className={label}>dootmaps administration</p>
          <h1 className="font-head text-xl font-black tracking-tight text-white sm:text-2xl">FLEET CONTROL SUITE</h1>
        </div>
        <button
          data-testid="auto-snap-toggle"
          onClick={toggleAutoSnap}
          disabled={busy === "snap"}
          className={`${ghostBtn} ml-auto disabled:opacity-50`}
          title="Auto-corrects trucks that drift off the road"
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: settings?.auto_snap_enabled ? "#7CE0A5" : "#FF6B6B" }}
          />
          Auto Snap Watch {settings?.auto_snap_enabled ? "On" : "Off"}
        </button>
        <button data-testid="snap-sweep-btn" onClick={snapSweep} disabled={busy === "sweep"} className={goldBtn}>
          <Radar size={12} /> {busy === "sweep" ? "Snapping..." : "Fleet Snap Sweep"}
        </button>
      </header>

      <div className="grid min-w-0 gap-5 p-4 sm:p-8 xl:grid-cols-2">
        {/* 1. positioning controls */}
        <section className={`${card} min-w-0`} data-testid="positioning-controls">
          <p className={label}>positioning controls</p>
          <h2 className="font-head mt-1 text-lg font-bold text-white">Drag a truck along its route</h2>

          <select
            data-testid="position-vehicle-select"
            value={posVehicle}
            onChange={(e) => setPosVehicle(e.target.value)}
            className={`${field} mt-4`}
          >
            <option value="">Select a truck</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.plate_number} {v.active_trip ? `· ${v.active_trip.destination_name}` : "· no trip"}
              </option>
            ))}
          </select>

          {trip ? (
            <>
              <div className="mt-4 flex items-center justify-between text-[11px]">
                <span>{trip.distance_covered_km} km covered</span>
                <span className="text-[#C8A24A]">{pct}%</span>
                <span>{trip.distance_remaining_km} km left</span>
              </div>
              <input
                data-testid="position-slider"
                type="range"
                min="0"
                max="100"
                value={pct}
                onChange={(e) => setPct(Number(e.target.value))}
                onMouseUp={() => applyPosition({ progress_pct: pct }, `Moved to ${pct}% of route`)}
                onTouchEnd={() => applyPosition({ progress_pct: pct }, `Moved to ${pct}% of route`)}
                className="mt-2 w-full accent-[#C8A24A]"
                style={{ accentColor: GOLD }}
              />
              <div className="mt-4 flex flex-wrap gap-2">
                {[-5, -1, 1, 5].map((km) => (
                  <button
                    key={km}
                    data-testid={`position-shift-${km}`}
                    disabled={busy === "pos"}
                    onClick={() => applyPosition({ shift_km: km }, `Shifted ${km > 0 ? "+" : ""}${km} km`)}
                    className={ghostBtn}
                  >
                    {km > 0 ? `+${km} km` : `${km} km`}
                  </button>
                ))}
                <button
                  data-testid="position-apply-btn"
                  disabled={busy === "pos"}
                  onClick={() => applyPosition({ progress_pct: pct }, `Moved to ${pct}% of route`)}
                  className={goldBtn}
                >
                  <Crosshair size={12} /> Apply {pct}%
                </button>
              </div>
            </>
          ) : (
            <p className="mt-4 text-[11px] text-[#C8A24A]/70">
              {posVehicle ? "This truck has no active route to move along." : "Pick a truck to enable the slider."}
            </p>
          )}
        </section>

        {/* 2. schedule planner */}
        <section className={`${card} min-w-0`} data-testid="schedule-planner">
          <p className={label}>schedule planner</p>
          <h2 className="font-head mt-1 text-lg font-bold text-white">Weekly status windows</h2>

          <form onSubmit={savePlan} className="mt-4 space-y-3">
            <div className="grid grid-cols-4 gap-1 sm:grid-cols-7" data-testid="planner-days">
              {DAYS.map((d, i) => (
                <button
                  key={d}
                  type="button"
                  data-testid={`planner-day-${d}`}
                  onClick={() => setPlanDay(i)}
                  className={`py-2 text-[10px] font-bold uppercase tracking-[0.1em] transition-colors duration-150 ${
                    planDay === i
                      ? "bg-[#C8A24A] text-[#0B1B33]"
                      : "border border-[#C8A24A]/30 text-[#C8A24A] hover:bg-[#C8A24A]/10"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <select
                data-testid="planner-status"
                value={planStatus}
                onChange={(e) => setPlanStatus(e.target.value)}
                className={field}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace("_", " ").toUpperCase()}
                  </option>
                ))}
              </select>
              <input data-testid="planner-from" type="time" value={from} onChange={(e) => setFrom(e.target.value)} className={field} />
              <input data-testid="planner-to" type="time" value={to} onChange={(e) => setTo(e.target.value)} className={field} />
            </div>

            <div className="doot-scroll max-h-28 overflow-y-auto border border-[#C8A24A]/25 pb-1" data-testid="planner-vehicles">
              {vehicles.map((v) => (
                <label
                  key={v.id}
                  className="flex cursor-pointer items-center gap-2 border-b border-[#C8A24A]/10 px-3 py-1.5 text-[11px] hover:bg-[#C8A24A]/5"
                >
                  <input
                    type="checkbox"
                    data-testid={`planner-vehicle-${v.plate_number}`}
                    checked={planVehicles.includes(v.id)}
                    onChange={() =>
                      setPlanVehicles((p) => (p.includes(v.id) ? p.filter((x) => x !== v.id) : [...p, v.id]))
                    }
                    style={{ accentColor: GOLD }}
                  />
                  {v.plate_number}
                  <span className="ml-auto text-[9px] uppercase tracking-[0.15em] text-[#C8A24A]/60">{v.status}</span>
                </label>
              ))}
            </div>

            <div className="pt-3">
              <LocationSearch label="park / workshop location (optional)" value={place} onChange={setPlace} testid="planner-place" />
            </div>
            <input
              data-testid="planner-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Note for the audit log"
              className={field}
            />
            <div className="grid gap-2 sm:grid-cols-2">
              <button type="submit" disabled={busy === "plan"} data-testid="planner-save-btn" className={goldBtn}>
                <CalendarClock size={12} /> {busy === "plan" ? "Saving..." : "Add to planner"}
              </button>
              <button
                type="button"
                onClick={copyWeek}
                disabled={busy === "copy"}
                data-testid="planner-copy-week-btn"
                className={ghostBtn}
                title="Duplicate this week's windows into next week"
              >
                <CopyPlus size={12} /> {busy === "copy" ? "Copying..." : "Copy Week"}
              </button>
            </div>
          </form>

          <div className="doot-scroll mt-4 max-h-52 overflow-y-auto" data-testid="planner-list">
            {schedules.length === 0 && <p className="text-[11px] text-[#C8A24A]/60">No windows scheduled yet.</p>}
            {schedules.map((s) => (
              <div
                key={s.id}
                data-testid={`planner-row-${s.id}`}
                className="flex items-center gap-2 border-b border-[#C8A24A]/10 py-2 text-[11px]"
              >
                <span className="font-bold text-white">{plate(s.vehicle_id)}</span>
                <span className="text-[#C8A24A]">{s.status.replace("_", " ").toUpperCase()}</span>
                <span className="text-[#F3E7C9]/70">
                  {new Date(s.from_at).toLocaleString()} → {s.to_at ? new Date(s.to_at).toLocaleTimeString() : "open"}
                </span>
                {s.location_name && (
                  <span className="flex items-center gap-1 text-[#F3E7C9]/60">
                    <MapPin size={10} /> {s.location_name}
                  </span>
                )}
                {s.active && <span className="text-[#7CE0A5]">ACTIVE</span>}
                <button
                  data-testid={`planner-delete-${s.id}`}
                  onClick={() => removePlan(s.id)}
                  className="ml-auto text-[#C8A24A] hover:text-[#FF6B6B]"
                  aria-label="Delete schedule"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* 3. draggable map positioning */}
        <section className={`${card} min-w-0 xl:col-span-2`} data-testid="drag-position-panel">
          <div className="flex flex-wrap items-center gap-2">
            <Move size={14} className="text-[#C8A24A]" />
            <div>
              <p className={label}>map positioning</p>
              <h2 className="font-head text-lg font-bold text-white">Drag the truck to its real spot</h2>
            </div>
            <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[#C8A24A]/70">
              {busy === "drag" ? "Saving override..." : selected ? selected.plate_number : "select a truck above"}
            </span>
            {undoPos && (
              <button
                data-testid="drag-undo-btn"
                onClick={undoDrag}
                disabled={busy === "undo"}
                className={ghostBtn}
                title="Put the truck back where it was before the drag"
              >
                <RotateCcw size={12} /> {busy === "undo" ? "Restoring..." : `Undo move · ${undoPos.plate}`}
              </button>
            )}
          </div>
          {selected?.current_location ? (
            <div className="mt-4 border border-[#C8A24A]/25">
              <AdminDragMap
                position={selected.current_location}
                plate={selected.plate_number}
                route={trip?.route_coords || []}
                onDrop={dropPin}
              />
            </div>
          ) : (
            <p className="mt-4 text-[11px] text-[#C8A24A]/70">
              Pick a truck in Positioning Controls to move it on the map.
            </p>
          )}
        </section>

        {/* 4. audit log */}
        <section className={`${card} min-w-0 xl:col-span-2`} data-testid="audit-log">
          <div className="flex items-center gap-2">
            <History size={14} className="text-[#C8A24A]" />
            <div>
              <p className={label}>override audit log</p>
              <h2 className="font-head text-lg font-bold text-white">Every manual change, with who and when</h2>
            </div>
            <button onClick={load} className={`${ghostBtn} ml-auto`} data-testid="audit-refresh-btn">
              Refresh
            </button>
            <button
              onClick={exportAudit}
              disabled={busy === "csv"}
              className={goldBtn}
              data-testid="audit-export-btn"
            >
              <Download size={12} /> {busy === "csv" ? "Exporting..." : "Export CSV"}
            </button>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5" data-testid="audit-filters">
            <select
              data-testid="audit-filter-vehicle"
              value={filters.vehicle_id}
              onChange={(e) => setFilters((f) => ({ ...f, vehicle_id: e.target.value }))}
              className={field}
            >
              <option value="">All trucks</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate_number}
                </option>
              ))}
            </select>
            <select
              data-testid="audit-filter-action"
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
              className={field}
            >
              <option value="">All actions</option>
              {actions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <input
              data-testid="audit-filter-from"
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              className={field}
            />
            <input
              data-testid="audit-filter-to"
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              className={field}
            />
            <button
              data-testid="audit-filter-clear"
              onClick={() => setFilters({ vehicle_id: "", action: "", date_from: "", date_to: "" })}
              className={ghostBtn}
            >
              Clear filters
            </button>
          </div>
          <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#C8A24A]/60" data-testid="audit-count">
            {log.length} entr{log.length === 1 ? "y" : "ies"} shown · CSV export uses these filters
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="audit-saved-views">
            <input
              data-testid="audit-view-name"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              placeholder="Name this filter view"
              className={`${field} max-w-[220px]`}
            />
            <button data-testid="audit-view-save" onClick={saveView} className={ghostBtn}>
              <Save size={12} /> Save view
            </button>
            {views.map((v) => (
              <span
                key={v.id}
                data-testid={`audit-view-chip-${v.id}`}
                className="flex items-center gap-2 border border-[#C8A24A]/35 px-2 py-1 text-[10px] uppercase tracking-[0.15em]"
              >
                <button onClick={() => applyView(v)} className="text-[#F3E7C9] hover:text-[#C8A24A]">
                  {v.name}
                </button>
                <button
                  data-testid={`audit-view-delete-${v.id}`}
                  onClick={() => removeView(v.id)}
                  className="text-[#C8A24A]/70 hover:text-[#FF6B6B]"
                  aria-label="Delete saved view"
                >
                  <Trash2 size={10} />
                </button>
              </span>
            ))}
          </div>
          <div className="doot-scroll mt-4 max-h-80 overflow-y-auto overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-[11px]">
              <thead>
                <tr className="text-[9px] uppercase tracking-[0.2em] text-[#C8A24A]">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2 pr-3">Truck</th>
                  <th className="py-2 pr-3">By</th>
                  <th className="py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {log.length === 0 && (
                  <tr>
                    <td colSpan="5" className="py-3 text-[#C8A24A]/60">
                      No overrides recorded yet.
                    </td>
                  </tr>
                )}
                {log.map((a) => (
                  <tr key={a.id} data-testid={`audit-row-${a.id}`} className="border-b border-[#C8A24A]/10">
                    <td className="py-2 pr-3 whitespace-nowrap text-[#F3E7C9]/70">
                      {new Date(a.created_at).toLocaleString()}
                    </td>
                    <td className="py-2 pr-3 font-bold text-[#C8A24A]">{a.action}</td>
                    <td className="py-2 pr-3 text-white">{a.plate_number || "fleet"}</td>
                    <td className="py-2 pr-3">{a.actor}</td>
                    <td className="py-2 text-[#F3E7C9]/70">
                      {Object.entries(a.details || {})
                        .filter(([, v]) => v !== null && v !== undefined)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(" · ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* 5. snap report */}
        <section className={`${card} min-w-0`} data-testid="snap-report">
          <div className="flex items-center gap-2">
            <ShieldAlert size={14} className="text-[#C8A24A]" />
            <div>
              <p className={label}>snap report · last 7 days</p>
              <h2 className="font-head text-lg font-bold text-white">Which trucks drift off the road</h2>
            </div>
            <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-[#C8A24A]/70">
              {report?.total_events || 0} corrections · {report?.trucks_flagged || 0} flagged
            </span>
          </div>

          <div className="mt-3 space-y-2" data-testid="drift-alerts">
            {alerts.length === 0 ? (
              <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#C8A24A]/50">
                <BellRing size={11} /> no tracker alerts open
              </p>
            ) : (
              alerts.map((al) => (
                <div
                  key={al.id}
                  data-testid={`drift-alert-${al.id}`}
                  className="flex items-start gap-2 border-l-2 bg-[#C8A24A]/5 px-3 py-2 text-[11px]"
                  style={{ borderColor: al.severity === "critical" ? "#FF6B6B" : "#C8A24A" }}
                >
                  <BellRing size={12} className="mt-0.5 shrink-0 text-[#C8A24A]" />
                  <span className="text-[#F3E7C9]/85">{al.message}</span>
                  <button
                    data-testid={`drift-alert-ack-${al.id}`}
                    onClick={() => ackAlert(al.id)}
                    className="ml-auto shrink-0 text-[9px] font-bold uppercase tracking-[0.15em] text-[#C8A24A] hover:opacity-75"
                  >
                    Clear
                  </button>
                </div>
              ))
            )}
          </div>
          <div className="doot-scroll mt-4 max-h-72 overflow-y-auto overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-[11px]">
              <thead>
                <tr className="text-[9px] uppercase tracking-[0.2em] text-[#C8A24A]">
                  <th className="py-2 pr-3">Truck</th>
                  <th className="py-2 pr-3">Drifts</th>
                  <th className="py-2 pr-3">Avg off</th>
                  <th className="py-2 pr-3">Worst</th>
                  <th className="py-2 pr-3">Last</th>
                  <th className="py-2">Tracker</th>
                </tr>
              </thead>
              <tbody>
                {(report?.vehicles || []).length === 0 && (
                  <tr>
                    <td colSpan="6" className="py-3 text-[#C8A24A]/60">
                      No drift corrections in the last 7 days - every truck is holding the road.
                    </td>
                  </tr>
                )}
                {(report?.vehicles || []).map((r) => (
                  <tr
                    key={r.vehicle_id}
                    data-testid={`snap-report-row-${r.plate_number}`}
                    className="border-b border-[#C8A24A]/10"
                  >
                    <td className="py-2 pr-3 font-bold text-white">{r.plate_number}</td>
                    <td className="py-2 pr-3 text-[#C8A24A]">
                      {r.drift_events} <span className="text-[9px] text-[#F3E7C9]/50">({r.auto_events} auto)</span>
                    </td>
                    <td className="py-2 pr-3">{r.avg_offset_m} m</td>
                    <td className="py-2 pr-3">{r.worst_offset_m} m</td>
                    <td className="py-2 pr-3 whitespace-nowrap text-[#F3E7C9]/70">
                      {r.last_drift_at ? new Date(r.last_drift_at).toLocaleString() : "--"}
                    </td>
                    <td className="py-2">
                      <span
                        className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em]"
                        style={{
                          color: r.health === "faulty" ? "#FF6B6B" : r.health === "watch" ? "#C8A24A" : "#7CE0A5",
                          border: `1px solid ${
                            r.health === "faulty" ? "#FF6B6B" : r.health === "watch" ? "#C8A24A" : "#7CE0A5"
                          }55`,
                        }}
                      >
                        {r.health}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* 6. gps hardware feed */}
        <section className={`${card} min-w-0`} data-testid="hardware-feed">
          <div className="flex items-center gap-2">
            <KeyRound size={14} className="text-[#C8A24A]" />
            <div>
              <p className={label}>gps hardware feed</p>
              <h2 className="font-head text-lg font-bold text-white">Live channel for tracker devices</h2>
            </div>
          </div>
          <p className="mt-3 break-all border border-[#C8A24A]/25 bg-[#0B1B33] p-3 font-mono text-[10px] text-[#F3E7C9]/80">
            POST {API}/hardware/ping
            <br />
            X-Device-Key: &lt;device key&gt;
            <br />
            {'{ "lat": 20.13, "lng": 83.18, "speed_kmh": 42.5, "ignition_state": "on" }'}
          </p>
          <div className="doot-scroll mt-3 max-h-64 overflow-y-auto overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-[11px]">
              <thead>
                <tr className="text-[9px] uppercase tracking-[0.2em] text-[#C8A24A]">
                  <th className="py-2 pr-3">Truck</th>
                  <th className="py-2 pr-3">Device</th>
                  <th className="py-2 pr-3">Key</th>
                  <th className="py-2 pr-3">Last ping</th>
                  <th className="py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr
                    key={d.vehicle_id}
                    data-testid={`hardware-row-${d.plate_number}`}
                    className="border-b border-[#C8A24A]/10"
                  >
                    <td className="py-2 pr-3 font-bold text-white">{d.plate_number}</td>
                    <td className="py-2 pr-3 text-[#F3E7C9]/70">{d.device_id || "--"}</td>
                    <td className="max-w-[170px] truncate py-2 pr-3 font-mono text-[10px] text-[#C8A24A]">
                      {d.device_key ? (
                        <button
                          data-testid={`hardware-key-reveal-${d.plate_number}`}
                          onClick={() => setRevealed((r) => ({ ...r, [d.vehicle_id]: !r[d.vehicle_id] }))}
                          title="Click to reveal / hide"
                          className="hover:opacity-75"
                        >
                          {revealed[d.vehicle_id] ? d.device_key : `•••• ${d.device_key.slice(-4)}`}
                        </button>
                      ) : (
                        "not issued"
                      )}
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      {d.last_hw_ping_at ? (
                        <span style={{ color: d.online ? "#7CE0A5" : "#FF6B6B" }}>
                          {d.online
                            ? new Date(d.last_hw_ping_at).toLocaleTimeString()
                            : `offline ${d.offline_minutes >= 60 ? `${Math.floor(d.offline_minutes / 60)}h` : `${d.offline_minutes}m`}`}
                        </span>
                      ) : (
                        <span className="text-[#C8A24A]/50">never</span>
                      )}
                    </td>
                    <td className="py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          data-testid={`hardware-key-btn-${d.plate_number}`}
                          onClick={() => issueKey(d.vehicle_id)}
                          disabled={busy === "key"}
                          className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#C8A24A] hover:opacity-75"
                        >
                          {d.device_key ? "Rotate" : "Issue key"}
                        </button>
                        {d.device_key && (
                          <>
                            <button
                              data-testid={`hardware-sheet-btn-${d.plate_number}`}
                              onClick={() => printSheet(d.vehicle_id, d.plate_number)}
                              className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] text-[#F3E7C9]/80 hover:opacity-75"
                            >
                              <FileText size={10} /> Sheet
                            </button>
                            <button
                              data-testid={`hardware-test-btn-${d.plate_number}`}
                              onClick={() => testPing(d.vehicle_id, d.plate_number)}
                              disabled={busy === "test"}
                              className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] text-[#7CE0A5] hover:opacity-75"
                            >
                              <Zap size={10} /> Test
                            </button>
                            <button
                              data-testid={`hardware-revoke-btn-${d.plate_number}`}
                              onClick={() => revokeKey(d.vehicle_id)}
                              className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#FF6B6B]/80 hover:opacity-75"
                            >
                              Revoke
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <div className="h-6" style={{ background: NAVY_2 }} />
    </div>
  );
}
